import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo from "../Assets/carrerup_allInOne_cover_NoBg.png";
import "./PrivacyPolicy.css";

const UPDATED_AT = "1er août 2026";

export default function PrivacyPolicy() {
  return (
    <main className="privacy-page">
      <Helmet>
        <title>Politique de confidentialité | CarrerUp</title>
        <meta
          name="description"
          content="Politique de confidentialité de la plateforme web et de l'application mobile CarrerUp."
        />
      </Helmet>

      <header className="privacy-header">
        <Link to="/" aria-label="Retour à l'accueil CarrerUp">
          <img src={logo} alt="CarrerUp" />
        </Link>
        <Link className="privacy-home-link" to="/">
          Retour à l’accueil
        </Link>
      </header>

      <article className="privacy-card">
        <div className="privacy-intro">
          <span className="privacy-kicker">Protection des données</span>
          <h1>Politique de confidentialité</h1>
          <p>
            Cette politique explique comment CarrerUp collecte, utilise, conserve
            et protège vos données lorsque vous utilisez le site carrerup.com et
            l’application mobile CarrerUp.
          </p>
          <p className="privacy-date">Dernière mise à jour : {UPDATED_AT}</p>
        </div>

        <nav className="privacy-summary" aria-label="Sommaire">
          <a href="#data">Données collectées</a>
          <a href="#uses">Utilisation</a>
          <a href="#sharing">Partage et prestataires</a>
          <a href="#rights">Vos droits</a>
          <a href="#contact">Contact</a>
        </nav>

        <section>
          <h2>1. Responsable du traitement</h2>
          <p>
            CarrerUp est responsable du traitement des données personnelles
            réalisé au moyen de sa plateforme. Pour toute question relative à
            vos données, vous pouvez écrire à{" "}
            <a href="mailto:support@carrerup.com">support@carrerup.com</a>.
          </p>
        </section>

        <section id="data">
          <h2>2. Données que nous collectons</h2>
          <p>Selon votre utilisation et le rôle choisi, nous pouvons traiter :</p>
          <ul>
            <li>
              <strong>Données de compte :</strong> nom, prénom ou nom de la
              structure, nom d’utilisateur, adresse e-mail, numéro de téléphone,
              langue, pays et mot de passe chiffré.
            </li>
            <li>
              <strong>Données de profil professionnel ou sportif :</strong>
              rôle, sport, poste, niveau, spécialité, club, ligue, parcours,
              biographie, compétences, disponibilité et liens professionnels.
            </li>
            <li>
              <strong>Contenus :</strong> photo de profil, images, vidéos,
              publications, commentaires, mentions « J’aime » et liens partagés.
            </li>
            <li>
              <strong>Interactions sociales :</strong> abonnements, matching,
              candidatures, offres, partages, conversations et messages.
            </li>
            <li>
              <strong>Données de vérification :</strong> justificatifs transmis
              volontairement pour vérifier un profil, un club, un média, une
              marque ou une qualité professionnelle.
            </li>
            <li>
              <strong>Données techniques :</strong> adresse IP, type d’appareil,
              système d’exploitation, identifiants techniques, journaux
              d’erreurs, jeton de notification et informations de sécurité.
            </li>
            <li>
              <strong>Données d’usage :</strong> pages consultées, publications
              affichées et interactions utilisées pour faire fonctionner,
              sécuriser et améliorer le fil d’actualité.
            </li>
          </ul>
        </section>

        <section id="uses">
          <h2>3. Pourquoi nous utilisons ces données</h2>
          <p>Nous utilisons les données nécessaires pour :</p>
          <ul>
            <li>créer, vérifier et sécuriser votre compte ;</li>
            <li>afficher votre profil et les contenus que vous publiez ;</li>
            <li>faire fonctionner le fil d’actualité et la recherche ;</li>
            <li>permettre les abonnements, le matching et la mise en réseau ;</li>
            <li>gérer les offres, candidatures et fonctionnalités des clubs ;</li>
            <li>acheminer les messages et les contenus partagés ;</li>
            <li>
              envoyer les notifications que vous avez autorisées, par exemple
              pour un commentaire, un message, un abonnement ou une visite de
              profil ;
            </li>
            <li>prévenir les abus, la fraude et les atteintes à la sécurité ;</li>
            <li>diagnostiquer les erreurs et améliorer les performances.</li>
          </ul>
          <p>
            Ces traitements reposent, selon le cas, sur l’exécution du service
            demandé, votre consentement, notre intérêt légitime à sécuriser et à
            améliorer CarrerUp, ou le respect de nos obligations légales.
          </p>
        </section>

        <section>
          <h2>4. Visibilité des informations</h2>
          <p>
            Certaines informations de profil et publications sont visibles par
            les autres utilisateurs afin de permettre le réseautage. Les messages
            privés ne sont visibles que par leurs participants, sous réserve des
            opérations techniques et de sécurité nécessaires. Ne publiez pas
            d’informations sensibles que vous ne souhaitez pas rendre visibles.
          </p>
        </section>

        <section id="sharing">
          <h2>5. Partage des données et prestataires</h2>
          <p>
            CarrerUp ne vend pas vos données personnelles. Elles peuvent être
            traitées par des prestataires strictement nécessaires au service,
            notamment :
          </p>
          <ul>
            <li>
              <strong>Railway</strong>, pour l’hébergement de l’API, de la base de
              données et de certains fichiers ;
            </li>
            <li>
              <strong>Google Firebase</strong>, pour l’acheminement des
              notifications push et les services techniques associés ;
            </li>
            <li>
              <strong>Expo / EAS</strong>, pour la construction, la distribution
              et certains services techniques de l’application mobile ;
            </li>
            <li>
              les autorités compétentes, uniquement lorsqu’une obligation légale
              l’impose ou pour protéger les utilisateurs et la plateforme.
            </li>
          </ul>
          <p>
            Certains prestataires peuvent traiter des données hors de votre pays.
            Dans ce cas, nous nous appuyons sur les garanties et mécanismes de
            protection applicables fournis par ces prestataires.
          </p>
        </section>

        <section>
          <h2>6. Conservation</h2>
          <p>
            Les données du compte sont conservées pendant son utilisation, puis
            supprimées ou anonymisées lorsqu’elles ne sont plus nécessaires,
            sous réserve des délais imposés par la loi, de la prévention de la
            fraude et du règlement des litiges. Les durées peuvent varier selon
            la nature des données. Les sauvegardes techniques sont supprimées
            progressivement selon leur cycle de rotation.
          </p>
        </section>

        <section>
          <h2>7. Sécurité</h2>
          <p>
            Nous mettons en œuvre des mesures techniques et organisationnelles
            destinées à protéger les comptes, les échanges et les fichiers contre
            l’accès non autorisé, la perte et l’altération. Aucun système ne peut
            toutefois garantir une sécurité absolue. Protégez votre mot de passe
            et informez-nous rapidement de toute activité inhabituelle.
          </p>
        </section>

        <section>
          <h2>8. Notifications et autorisations mobiles</h2>
          <p>
            L’application demande votre autorisation avant d’envoyer des
            notifications. Vous pouvez les désactiver dans les réglages de votre
            appareil. L’accès aux photos, vidéos, fichiers ou à l’appareil photo
            n’est utilisé que lorsque vous choisissez une fonctionnalité qui le
            nécessite, par exemple publier un contenu ou modifier votre profil.
          </p>
        </section>

        <section id="rights">
          <h2>9. Vos droits et suppression du compte</h2>
          <p>
            Selon la législation applicable, vous pouvez demander l’accès, la
            rectification, l’effacement, la limitation ou l’opposition au
            traitement de vos données, ainsi que leur portabilité lorsque ce droit
            s’applique. Vous pouvez modifier plusieurs informations directement
            dans les paramètres de CarrerUp.
          </p>
          <p>
            Pour demander la suppression de votre compte et des données associées,
            écrivez à{" "}
            <a href="mailto:support@carrerup.com?subject=Suppression%20de%20mon%20compte%20CarrerUp">
              support@carrerup.com
            </a>{" "}
            depuis l’adresse liée au compte. Nous pourrons demander une
            vérification d’identité avant d’exécuter la demande.
          </p>
        </section>

        <section>
          <h2>10. Mineurs</h2>
          <p>
            CarrerUp n’est pas destiné aux enfants n’ayant pas l’âge légal requis
            pour consentir seuls au traitement de leurs données dans leur pays.
            Lorsqu’une autorisation parentale est légalement nécessaire, le
            représentant légal doit superviser la création et l’utilisation du
            compte.
          </p>
        </section>

        <section>
          <h2>11. Évolution de cette politique</h2>
          <p>
            Nous pouvons mettre cette politique à jour pour refléter l’évolution
            de CarrerUp, de ses prestataires ou des exigences légales. La date
            affichée en haut de cette page indique la dernière mise à jour. Une
            information complémentaire pourra être affichée dans l’application en
            cas de changement important.
          </p>
        </section>

        <section id="contact" className="privacy-contact">
          <h2>12. Nous contacter</h2>
          <p>
            Pour toute question ou demande concernant cette politique et vos
            données personnelles :
          </p>
          <a href="mailto:support@carrerup.com">support@carrerup.com</a>
        </section>
      </article>
    </main>
  );
}
